import { CommonModule, DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { FloatLabelModule } from 'primeng/floatlabel';
import { ListboxModule } from 'primeng/listbox';
import { SplitterModule } from 'primeng/splitter';
import { firstValueFrom, Subject } from 'rxjs';
import { SignalComponent } from '../../components/signal/signal.component';
import { Graph } from '../../models/graph';
import { ApiService } from '../../services/api.service';
import { UiNotificationService } from '../../services/ui-notification.service';
import { AlertMeasureEvent } from '../../models/alert';

@Component({
  selector: 'app-synoptic-table-dialog',
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    DividerModule,
    ButtonModule,
    TranslatePipe,
    SplitterModule,
    FloatLabelModule,
    DatePickerModule,
    ListboxModule,
    SignalComponent,
    DatePipe
  ],
  templateUrl: './synoptic-table-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: ``,
})
export class SynopticTableDialogComponent implements OnChanges {
  @Input() messageService!: UiNotificationService;
  @Input() userId: string = "";
  @Input() lastMeasure: number = 0;

  // visibilità dialog (bidirezionale)
  @Input() visible : boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  endDate: Date = new Date(Date.now());
  startDate: Date = new Date(Date.now() - (60 * 60 * 1000));

  loading: boolean = false;
  segnaleFreqCardiaca: Graph[][] = [];
  segnaleFreqRespiratoria: Graph[][] = [];
  segnaleSaturazione: Graph[][] = [];
  segnaleTemperatura: Graph[][] = [];
  events: AlertMeasureEvent[] = [];

  xMax: number = this.endDate.getTime();
  xMin: number = this.startDate.getTime();

  zoomLevel: Subject<any> = new Subject<any>();
  zoomCommand: Subject<number> = new Subject<number>();

  constructor(private apiService: ApiService) {
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['visible'] && this.visible && this.userId != "") {
      this.resetParameters();
      this.loadData();
    }
  }

  onApply() {
    this.setLimits();
    this.loadData();
  }

  onClose() {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  onZoomChanged(scales: any) {
    this.zoomLevel.next(scales);
  }

  onZoomCommand(event: number) {
    this.zoomCommand.next(event)
  }

  private async loadData() : Promise<void> {
    this.loading = true;

    try {
      const [fc, fr, o2, t, a] = await Promise.all([
        firstValueFrom(this.apiService.getMeasure(this.userId, this.startDate, this.endDate, 0)),
        firstValueFrom(this.apiService.getMeasure(this.userId, this.startDate, this.endDate, 1)),
        firstValueFrom(this.apiService.getMeasure(this.userId, this.startDate, this.endDate, 5)),
        firstValueFrom(this.apiService.getMeasure(this.userId, this.startDate, this.endDate, 4)),
        firstValueFrom(this.apiService.getAlertMeasures(this.userId, this.startDate, this.endDate))
      ]);

      this.segnaleFreqCardiaca = fc;
      this.segnaleFreqRespiratoria = fr;
      this.segnaleSaturazione = o2;
      this.segnaleTemperatura = t;
      this.events = a;

      this.loading = false;
    } catch (error: any) {
      this.loading = false;
      if (error instanceof HttpErrorResponse) {
        this.messageService.showError(error);
      } else {
        const err = error as Error;
        this.messageService.showErrorMessage(err.message);
      }
    }
  }

  private resetParameters() {
    this.endDate = new Date(this.lastMeasure + (5 * 60 * 1000));
    this.startDate = new Date(this.lastMeasure - (55 * 60 * 1000));

    this.setLimits();

    this.segnaleFreqCardiaca = [[]];
    this.segnaleFreqRespiratoria = [[]];
    this.segnaleSaturazione = [[]];
    this.segnaleTemperatura = [[]];
    this.events = [];
  }

  private setLimits() {
    this.xMax = this.endDate.getTime();
    this.xMin = this.startDate.getTime();
  }
}
